import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial'];
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
// ─── Line items + discount: encoded into service_description ─────────
// We persist the extra data (lineItems[], discount) as a JSON marker
// appended to service_description so we don't need a DB migration and
// existing saved receipts stay 100% backwards compatible:
//   <plain text description>
//   <!--__DETAILS__:{"items":[...],"discount":1234}-->
// On load, we split on the marker; the plain text stays in the
// description textarea, the JSON repopulates the structured fields.
const DETAILS_MARKER = '<!--__DETAILS__:';
const DETAILS_END = '-->';
type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
};
const newLineItem = (): LineItem => ({
  id: Math.random().toString(36).slice(2, 10),
  description: '',
  quantity: 1,
  unitPrice: 0
});
const encodeDescription = (plain: string, items: LineItem[], discount: number): string => {
  if (items.length === 0 && (!discount || discount === 0)) return plain;
  const payload = JSON.stringify({
    items: items.map(it => ({
      description: it.description,
      quantity: it.quantity,
      unitPrice: it.unitPrice
    })),
    discount: discount || 0
  });
  return `${plain}\n\n${DETAILS_MARKER}${payload}${DETAILS_END}`;
};
const decodeDescription = (raw: string): {
  plain: string;
  items: LineItem[];
  discount: number;
} => {
  if (!raw || !raw.includes(DETAILS_MARKER)) {
    return {
      plain: raw || '',
      items: [],
      discount: 0
    };
  }
  try {
    const idx = raw.indexOf(DETAILS_MARKER);
    const plain = raw.slice(0, idx).trimEnd();
    const after = raw.slice(idx + DETAILS_MARKER.length);
    const endIdx = after.indexOf(DETAILS_END);
    const json = endIdx >= 0 ? after.slice(0, endIdx) : after;
    const parsed = JSON.parse(json);
    const items: LineItem[] = (parsed.items || []).map((it: any) => ({
      id: Math.random().toString(36).slice(2, 10),
      description: String(it.description || ''),
      quantity: Number(it.quantity) || 0,
      unitPrice: Number(it.unitPrice) || 0
    }));
    return {
      plain,
      items,
      discount: Number(parsed.discount) || 0
    };
  } catch {
    return {
      plain: raw,
      items: [],
      discount: 0
    };
  }
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  // Line items and discount (new — optional desglose)
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  // Derived totals — when there are line items, the total is computed
  // from them minus the discount; the user's manually-entered
  // totalAmount becomes informational only and is auto-synced.
  const lineItemsSubtotal = lineItems.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const hasLineItems = lineItems.length > 0;
  const effectiveTotal = hasLineItems ? Math.max(0, lineItemsSubtotal - (Number(discount) || 0)) : formData.totalAmount;
  const remainingBalance = effectiveTotal - formData.paymentAmount;
  // Keep formData.totalAmount in sync with the computed total whenever
  // line items or discount change, so the existing flows (payment %,
  // saved totalAmount on the DB, etc.) keep working unchanged.
  useEffect(() => {
    if (hasLineItems) {
      setFormData(prev => {
        if (prev.totalAmount === effectiveTotal) return prev;
        return {
          ...prev,
          totalAmount: effectiveTotal
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemsSubtotal, discount, hasLineItems]);
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        // Encode line items + discount into service_description so the
        // existing DB schema stays untouched.
        service_description: encodeDescription(formData.serviceDescription, lineItems, discount),
        total_amount: effectiveTotal,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    const decoded = decodeDescription(receipt.service_description || '');
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: decoded.plain,
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    setLineItems(decoded.items);
    setDiscount(decoded.discount);
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
    setLineItems([]);
    setDiscount(0);
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  // Line item handlers
  const addLineItem = () => setLineItems(prev => [...prev, newLineItem()]);
  const updateLineItem = (id: string, field: keyof Omit<LineItem, 'id'>, value: string | number) => setLineItems(prev => prev.map(it => it.id === id ? {
    ...it,
    [field]: field === 'description' ? String(value) : Number(value) || 0
  } : it));
  const removeLineItem = (id: string) => setLineItems(prev => prev.filter(it => it.id !== id));
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-6076" data-mp="48u0xdkvjyyz">
      {/* Print Styles */}
      <style data-id="element-6077" data-mp="e4x66sb0wdq5">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-6078" data-mp="2cggrq1m1tp0v">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-6079" data-mp="fjzqj9yojsxr">
          <div className="flex items-center gap-4" data-id="element-6080" data-mp="ao2f9i29xcru">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-6081" data-mp="inp497i8lg20">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-6082" data-mp="8psv2l8v0v6j" />
            </button>
            <div data-id="element-6083" data-mp="8rvjj0ud6g95">
              <h1 className="text-xl font-bold text-white" data-id="element-6084" data-mp="dovp7anaxgzq">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-6085" data-mp="3w2bncjqbgy4g">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-6086" data-mp="4oph64adh97y">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-6087" data-mp="4pig4j33aufj">
              <RefreshCw className="w-4 h-4" data-id="element-6088" data-mp="3r1fpamsnc66y" />
              <span className="hidden sm:inline" data-id="element-6089" data-mp="3d52535wq9qos">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-6090" data-mp="3h648lyxogowk">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-6091" data-mp="37j9whqnzsj6h" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-6092" data-mp="32hknegkbyfl1" /> : <Save className="w-4 h-4" data-id="element-6093" data-mp="3hf4o3ba71ole" />}
              <span className="hidden sm:inline" data-id="element-6094" data-mp="3ch33glx4dh7t">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-6095" data-mp="3qakn06pt2txu">
              <MessageCircle className="w-4 h-4" data-id="element-6096" data-mp="3v82yo4v7jn9j" />
              <span className="hidden sm:inline" data-id="element-6097" data-mp="3v9e0hz53eky0">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-6098" data-mp="2svpy9pg0e9ge">
              <Printer className="w-4 h-4" data-id="element-6099" data-mp="31sqlhsrce1i9" />
              <span className="hidden sm:inline" data-id="element-6100" data-mp="36ltd3vck70vt">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-6101" data-mp="3bk4ls2r4b5cg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-6102" data-mp="3gj7rlpex9flh">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-6103" data-mp="3kkt59cza5xwq">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-6104" data-mp="3kj8bhpksiizx">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-6105" data-mp="3pjmhl22tzivb">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-6106" data-mp="1q8uuh6lbotyq" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-6107" data-mp="1v8h65bli625f">
                <div className="grid grid-cols-2 gap-4" data-id="element-6108" data-mp="1nndnx2f021sn">
                  <div data-id="element-6109" data-mp="1noyg4ldud8nc">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6110" data-mp="1iw59tt2q5s95">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6111" data-mp="1dxlp1vzvamid" />
                  </div>
                  <div data-id="element-6112" data-mp="l8eqix0exwx4">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6113" data-mp="l8wn5uqzmd5k">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6114" data-mp="h5i2gqs3t8ls" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-6115" data-mp="341nl79nt8n3j">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6116" data-mp="340mbp9mg08nx">
                    Cliente
                  </h3>
                  <div data-id="element-6117" data-mp="2v8k8x4owigah">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6118" data-mp="2v7sg21mouwnw">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6119" data-mp="3np4t3517ihs2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-6120" data-mp="3a3h4jotobv3t">
                    <div data-id="element-6121" data-mp="3a51zvgntbi2q">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6122" data-mp="3a5tuao5ob9rf">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6123" data-mp="3j3dzg86npq7b" />
                    </div>
                    <div data-id="element-6124" data-mp="23hzj1xbqdwjw">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6125" data-mp="23hhn71szdkci">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6126" data-mp="2ciflr4kngznp" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-6127" data-mp="3jtjvlfn396z3">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6128" data-mp="3jsh87hoz5mnx">
                    Servicio
                  </h3>
                  <div data-id="element-6129" data-mp="39vcgz3g9v109">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6130" data-mp="39uungekzv0w1">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6131" data-mp="2w2egx3svwkqd">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-6132" data-mp="2d5vdmv4rhr1q">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-6133" data-mp="npsqvb99eqg0">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6134" data-mp="nq2kr9xfdhqz">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-6135" data-mp="dzgalv8h9jfo" />
                  </div>
                </div>

                {/* ─── Desglose (Line Items) ───────────────────── */}
                <div className="space-y-3" data-id="element-6136" data-mp="3ddjos97e2bk6">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2" data-id="element-6137" data-mp="3dcialvv2t8y8">
                    <h3 className="text-sm font-semibold text-white/80" data-id="element-6138" data-mp="1swkcwqejsbec">
                      Desglose del Cobro{' '}
                      <span className="text-xs text-white/40 font-normal" data-id="element-6139" data-mp="1xv400u4vnt8a">
                        (Opcional)
                      </span>
                    </h3>
                    <button type="button" onClick={addLineItem} className="text-xs font-medium text-[#C9A34E] hover:text-[#C9A34E]/80 flex items-center gap-1" data-id="element-6140" data-mp="26n5umbs9mfan">
                      <Plus className="w-3.5 h-3.5" data-id="element-6141" data-mp="2gn6j0c4zrllp" />
                      Agregar línea
                    </button>
                  </div>

                  {lineItems.length === 0 ? <p className="text-xs text-white/40 italic" data-id="element-6142" data-mp="2pkyybgapqt93">
                      Sin desglose. El recibo usará el monto total único de
                      arriba. Agregá líneas para desglosar por
                      producto/servicio.
                    </p> : <div className="space-y-2" data-id="element-6143" data-mp="3w1dkuc6f6j64">
                      {lineItems.map((it, idx) => {
                    const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                    return <div key={it.id} className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-6144" data-mp="4xynkkzxv1b5" data-mpi={idx}>
                            <div className="flex items-center justify-between" data-id="element-6145" data-mp="38ep1rn5ocknx">
                              <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold" data-id="element-6146" data-mp="3n3bowb1jmgpp">
                                Línea {idx + 1}
                              </span>
                              <button type="button" onClick={() => removeLineItem(it.id)} className="text-white/30 hover:text-red-400 transition-colors" aria-label="Eliminar línea" data-id="element-6147" data-mp="2ul7ebr9vansm">
                                <Trash2 className="w-3.5 h-3.5" data-id="element-6148" data-mp="19wvc1670nrc1" />
                              </button>
                            </div>
                            <input type="text" value={it.description} onChange={e => updateLineItem(it.id, 'description', e.target.value)} placeholder="Descripción del producto/servicio" className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6149" data-mp="105hcwziz8tjf" />
                            <div className="grid grid-cols-3 gap-2" data-id="element-6150" data-mp="2a4a29b3gguvn">
                              <div data-id="element-6151" data-mp="2a5l61awwi2mr">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6152" data-mp="254dxvaquj1fk">
                                  Cantidad
                                </label>
                                <input type="number" min={0} step={1} value={it.quantity} onChange={e => updateLineItem(it.id, 'quantity', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6153" data-mp="1wclvzofw10ml" />
                              </div>
                              <div data-id="element-6154" data-mp="yikto271q44r">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6155" data-mp="yhjilxq15hn1">
                                  Precio Unit. ₡
                                </label>
                                <input type="number" min={0} value={it.unitPrice} onChange={e => updateLineItem(it.id, 'unitPrice', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6156" data-mp="17a37w7kfm9v7" />
                              </div>
                              <div data-id="element-6157" data-mp="1bkvpdiukxog0">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6158" data-mp="16oycjmiiz07z">
                                  Subtotal
                                </label>
                                <div className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-[#C9A34E] text-sm font-bold" data-id="element-6159" data-mp="1fp3fnkgorcgc">
                                  {formatCurrency(subtotal)}
                                </div>
                              </div>
                            </div>
                          </div>;
                  })}

                      {/* Subtotal + Discount summary inside form */}
                      <div className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-6160" data-mp="1zaq11x35x6yo">
                        <div className="flex justify-between text-xs text-white/60" data-id="element-6161" data-mp="92hicq5392qg">
                          <span data-id="element-6162" data-mp="50mf3g3lkfa1">Subtotal líneas</span>
                          <span className="font-bold text-white" data-id="element-6163" data-mp="3r00ks0if57xy">
                            {formatCurrency(lineItemsSubtotal)}
                          </span>
                        </div>
                        <div data-id="element-6164" data-mp="3haz8akb318oq">
                          <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6165" data-mp="3h9o44jdq5yx3">
                            Descuento ₡
                          </label>
                          <input type="number" min={0} value={discount} onChange={e => setDiscount(Number(e.target.value) || 0)} placeholder="0" className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6166" data-mp="37brtdsop95zi" />
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-white/10" data-id="element-6167" data-mp="11gyvqids9hw1">
                          <span className="text-white font-semibold" data-id="element-6168" data-mp="28a2426tcvr4j">
                            Total
                          </span>
                          <span className="font-bold text-[#C9A34E]" data-id="element-6169" data-mp="2d8vm8cjl86b1">
                            {formatCurrency(effectiveTotal)}
                          </span>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* ─── Existing Pago section ──────────────────── */}
                <div className="space-y-4" data-id="element-6170" data-mp="2r2d3fqosx6xw">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6171" data-mp="2w0obpwzg79e0">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-6172" data-mp="30wdgceqbrgh6">
                    <div data-id="element-6173" data-mp="11mx576cf15ms">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6174" data-mp="11nor1pg7nozv">
                        Monto Total (Contrato)
                        {hasLineItems && <span className="ml-1 text-[10px] text-white/40" data-id="element-6175" data-mp="3jwj90sr6sjmr">
                            (auto)
                          </span>}
                      </label>
                      <div className="relative" data-id="element-6176" data-mp="3b368b4mvsdq4">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-6177" data-mp="363tpcuz4rqst">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} disabled={hasLineItems} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-6178" data-mp="6nn92ylslf38" />
                      </div>
                    </div>
                    <div data-id="element-6179" data-mp="2jss9zgcpzqep">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-6180" data-mp="25vwp8qn3wait">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-6181" data-mp="2awklbqq6zhjw">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-6182" data-mp="1w4itmty9zuuy">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6183" data-mp="1n4vrg5xq2l0u" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6184" data-mp="18jhtdx9g13ie">
                    <div data-id="element-6185" data-mp="13iu3xg0v4uqa">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6186" data-mp="13hiuazkqksr9">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6187" data-mp="1ckblz3xtz6hm">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-6188" data-mp="3podu187vl5aj">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-6189" data-mp="3d9ohwlze2x1">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6190" data-mp="8ai4prnkjwvn">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6191" data-mp="36tqijrtcs9c1">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-6192" data-mp="2khx7z3h9cd86">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-6193" data-mp="2bok5paxazz9f">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6194" data-mp="26s27ktwhaz62">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6195" data-mp="21u0jtvo3ybo9" />
                  </div>

                  <div data-id="element-6196" data-mp="543kmxtwcnn5">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6197" data-mp="9woa5n5rsekv">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-6198" data-mp="3scqplnlh9pl0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-6199" data-mp="34lbfv9rdg5zq">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-6200" data-mp="2x1sg3rtwry4g">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-6201" data-mp="3628mb921djd0">
                  <List className="w-5 h-5 text-blue-400" data-id="element-6202" data-mp="313e0dy8rveoo" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-6203" data-mp="2ddt6ikb2yx0m">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-6204" data-mp="2n3w8vb43dnbm" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-6205" data-mp="2id615r8718dr" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-6206" data-mp="1ulqwvswuwgzv">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-6207" data-mp="152oqbtkl1l61">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-6208" data-mp="154hvt5jglzcg" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-6209" data-mp="1e21zsm8pi9qr">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-6210" data-mp="1o1syl04avawn">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-6211" data-mp="1s23122svvj6r">
                          <div data-id="element-6212" data-mp="26po4jxto7kf7">
                            <div className="flex items-center gap-2 mb-1" data-id="element-6213" data-mp="2bqlw310klqtd">
                              <span className="text-sm font-bold text-white" data-id="element-6214" data-mp="2bohifrcvddfw">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-6215" data-mp="3r88g1knontpr">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-6216" data-mp="iujag7po4hih">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-6217" data-mp="dwzvf5l5c0ph">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-6218" data-mp="wsq1p5043j96">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-6219" data-mp="wry3rnj1l9fq">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6220" data-mp="2n9hk67yqjkgl">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-6221" data-mp="1zive2m8aka98">
            <div className="sticky top-24 print:static" data-id="element-6222" data-mp="1zjdii653xus8">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-6223" data-mp="1uktwy6ufel0e">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-6224" data-mp="3oxtjyl14yhhp">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-6225" data-mp="a29mmnc5ghrf">
                  <div data-id="element-6226" data-mp="a42u24ul760h">
                    <div className="flex items-center gap-2 mb-2" data-id="element-6227" data-mp="f0taitab03mf">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-6228" data-mp="f3738vhih3qt">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-6229" data-mp="3ihtcdcdm3i1q">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-6230" data-mp="3niz69dj0dbcu">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-6231" data-mp="i0l4sw6npgzx">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-6232" data-mp="d4m8mmf36uoy">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-6233" data-mp="ryhd7g87lxuc">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-6234" data-mp="n20oajt6rfg7">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-6235" data-mp="myuuwsdixreo">
                      Recibo
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-6236" data-mp="3qiqw948bw2kj">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-6237" data-mp="92r5dcdli78j">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-6238" data-mp="37qoe1og1ge86">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6239" data-mp="1n72r3w13lgfd">
                    Recibí de:
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-6240" data-mp="1dbrepkd5wlas">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-6241" data-mp="25zdq3esmsu87">
                    {formData.clientId && <p data-id="element-6242" data-mp="2az01rjsta2ew">
                        <span className="font-semibold" data-id="element-6243" data-mp="2ax5ie9x6uicx">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-6244" data-mp="20yps295zjd4h">
                        <span className="font-semibold" data-id="element-6245" data-mp="212575bznsm5w">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details — line items table or legacy */}
                <div className="mb-8" data-id="element-6246" data-mp="15tl4pvcrddjl">
                  <table className="w-full text-left border-collapse" data-id="element-6247" data-mp="1111o0piry2ak">
                    <thead data-id="element-6248" data-mp="10zql0rx5kyki">
                      <tr className="border-b-2 border-gray-200" data-id="element-6249" data-mp="w1xetyvuo4k8">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-6250" data-mp="w047sihbribp">
                          Descripción
                        </th>
                        {hasLineItems && <>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-center w-16" data-id="element-6251" data-mp="1jrrok7kvhd09">
                              Cant.
                            </th>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-28" data-id="element-6252" data-mp="1as4phsfm8jad">
                              Precio
                            </th>
                          </>}
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-32" data-id="element-6253" data-mp="3vgao2eunxu1a">
                          {hasLineItems ? 'Subtotal' : 'Total Contrato'}
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6254" data-mp="2jzg3lwqmehnz">
                      {hasLineItems ? <>
                          {/* Header row with service type as group title */}
                          <tr className="border-b border-gray-100" data-id="element-6255" data-mp="2tqu8kk1cc9oi">
                            <td colSpan={4} className="py-3" data-id="element-6256" data-mp="2yp44iwuw3eb6">
                              <p className="font-bold text-gray-800" data-id="element-6257" data-mp="2ynknh41esz8c">
                                {formData.serviceType}
                              </p>
                              {formData.serviceDescription && <p className="text-sm text-gray-500 mt-1" data-id="element-6258" data-mp="37ng4vqlk5qgt">
                                  {formData.serviceDescription}
                                </p>}
                            </td>
                          </tr>
                          {lineItems.map(it => {
                        const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                        return <tr key={it.id} className="border-b border-gray-100" data-id="element-6259" data-mp="2kojwobge38y1">
                                <td className="py-3 pr-4 text-sm text-gray-700" data-id="element-6260" data-mp="2zj80ll655zbj">
                                  {it.description || <span className="italic text-gray-400" data-id="element-6261" data-mp="38e4ir37uambj">
                                      Sin descripción
                                    </span>}
                                </td>
                                <td className="py-3 text-center text-sm text-gray-700" data-id="element-6262" data-mp="3i9y3vazmjo4x">
                                  {it.quantity}
                                </td>
                                <td className="py-3 text-right text-sm text-gray-700" data-id="element-6263" data-mp="uog9a5oknip">
                                  {formatCurrency(it.unitPrice)}
                                </td>
                                <td className="py-3 text-right text-sm font-semibold text-gray-800" data-id="element-6264" data-mp="3mzjrz21hcvlu">
                                  {formatCurrency(subtotal)}
                                </td>
                              </tr>;
                      })}
                        </> : <tr className="border-b border-gray-100" data-id="element-6265" data-mp="3j0t1vevfme88">
                          <td className="py-4 pr-4" data-id="element-6266" data-mp="34beugcbf26vh">
                            <p className="font-bold text-gray-800 mb-1" data-id="element-6267" data-mp="398x2m0hm0i2f">
                              {formData.serviceType}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-6268" data-mp="2va754jqvogdj">
                              {formData.serviceDescription}
                            </p>
                          </td>
                          <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-6269" data-mp="3fpzvyakbc9xs">
                            {formatCurrency(formData.totalAmount)}
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-6270" data-mp="2ws5ssd5v8snb">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-6271" data-mp="2wtqkzw4pjzi0">
                    {hasLineItems && <>
                        <div className="flex justify-between text-sm text-gray-600" data-id="element-6272" data-mp="2n1kukqu3tq4x">
                          <span data-id="element-6273" data-mp="2i2rhgziqc15a">Subtotal:</span>
                          <span data-id="element-6274" data-mp="2i42jouwj10ua">{formatCurrency(lineItemsSubtotal)}</span>
                        </div>
                        {discount > 0 && <div className="flex justify-between text-sm text-red-600" data-id="element-6275" data-mp="2921o02gxymuq">
                            <span data-id="element-6276" data-mp="2945yx4d9cm4i">Descuento:</span>
                            <span data-id="element-6277" data-mp="oiz8uxxxsx28">-{formatCurrency(discount)}</span>
                          </div>}
                      </>}
                    <div className="flex justify-between text-sm text-gray-600" data-id="element-6278" data-mp="xkyr1tkou76r">
                      <span data-id="element-6279" data-mp="xiw3rhax6s5q">Total del Contrato:</span>
                      <span data-id="element-6280" data-mp="12iqr5avhkzm1">{formatCurrency(effectiveTotal)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-6281" data-mp="4x2xgxqf0ve7">
                      <span data-id="element-6282" data-mp="9t1sh4691bnl">Monto Pagado:</span>
                      <span data-id="element-6283" data-mp="etxu1b34olpu">{formatCurrency(formData.paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-6284" data-mp="jl7hc517fcyk">
                      <span data-id="element-6285" data-mp="3ibg2nkqq5wrb">Saldo Pendiente:</span>
                      <span data-id="element-6286" data-mp="3iao7gb11i11k">{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-6287" data-mp="2x0n0ha6jg6w0">
                  <div data-id="element-6288" data-mp="31zq54tilwb3g">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6289" data-mp="31xvo3qagh34n">
                      Detalles del Pago
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-6290" data-mp="2s0hi6km5rwql">
                      <span className="font-semibold" data-id="element-6291" data-mp="2s33oyi18687r">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    <p className="text-sm text-gray-800" data-id="element-6292" data-mp="2j0uf7uds6ivf">
                      <span className="font-semibold" data-id="element-6293" data-mp="2j2fa5l40c3tt">Método:</span>{' '}
                      {formData.paymentMethod}
                    </p>
                    {formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-6294" data-mp="29asth98whcjo">
                        <span className="font-semibold" data-id="element-6295" data-mp="1vbt1psq3chja">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-6296" data-mp="1gq5gs5c2d8zd">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6297" data-mp="1bp7zduyvoiyx">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-6298" data-mp="1pqako5i1tp8s">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-6299" data-mp="1uo3r8zn9kl9l">
                  <div data-id="element-6300" data-mp="29d8bsnhj71kv">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-6301" data-mp="29boviwvvkqj3">
                      Este recibo es un comprobante de pago válido.
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-6302" data-mp="2ideo461htvig">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-6303" data-mp="1b1ytz2brlxwn">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-6304" data-mp="1g0idyx6sszmd">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-6305" data-mp="1k0j3bcvfqv87">
                        {formData.paymentAmount >= effectiveTotal ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}